import React, { useState, useEffect, useCallback} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'react-hot-toast';
import { Loader2, ArrowLeft, ReceiptText, Package, DollarSign, CheckCircle2, FileCheck, Clock, XCircle, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreditMemoView = () => {
  const { creditMemoNumber } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [memo, setMemo] = useState(null);


  const loadMemo = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/otc/credit-memos/${creditMemoNumber}`);
      setMemo(response.data);
    } catch (error) {
      console.error('Error loading credit memo:', error);
      toast.error('Failed to load credit memo');
    } finally {
      setIsLoading(false);
    }
  }, [creditMemoNumber]);

  useEffect(() => {
    loadMemo();
  }, [loadMemo]);

  const handleRelease = async () => {
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/otc/credit-memos/${creditMemoNumber}/release`);
      setMemo(response.data);
      toast.success('Credit memo released successfully');
    } catch (error) {
      console.error('Error releasing credit memo:', error);
      toast.error(error.response?.data?.detail || 'Failed to release credit memo');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePost = async () => {
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/otc/credit-memos/${creditMemoNumber}/post`);
      setMemo(response.data);
      toast.success('Credit memo posted - AR open item created');
    } catch (error) {
      console.error('Error posting credit memo:', error);
      toast.error(error.response?.data?.detail || 'Failed to post credit memo');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'CREATED': { color: 'bg-orange-100 text-orange-800', icon: Clock },
      'RELEASED': { color: 'bg-yellow-100 text-yellow-800', icon: FileCheck },
      'POSTED': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      'CANCELLED': { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', icon: ReceiptText };
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${badge.color}`}>
        <Icon className="w-4 h-4" /> {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading credit memo...</span>
        </div>
      </Layout>
    );
  }

  if (!memo) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Credit Memo Not Found</h1>
          <Button onClick={() => navigate('/otc/credit-memos')}>Back to Credit Memos</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="credit-memo-view">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/otc/credit-memos')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <ReceiptText className="w-6 h-6" />
                Credit Memo {memo.credit_memo_number}
              </h1>
              <p className="text-sm text-gray-600">Billing Type: {memo.billing_type} (Credit Memo)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(memo.status)}
          </div>
        </div>

        {/* Status Flow & Actions */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                ['CREATED', 'RELEASED', 'POSTED'].includes(memo.status) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>1</span>
              <span className="text-sm font-medium">Created</span>
              <span className="mx-2 text-gray-300">→</span>
              
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                ['RELEASED', 'POSTED'].includes(memo.status) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>2</span>
              <span className="text-sm font-medium">Released</span>
              <span className="mx-2 text-gray-300">→</span>
              
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                memo.status === 'POSTED' ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>3</span>
              <span className="text-sm font-medium">Posted</span>
            </div>
            
            <div className="flex gap-2">
              {memo.status === 'CREATED' && (
                <Button onClick={handleRelease} disabled={isProcessing}>
                  {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck className="w-4 h-4 mr-2" />}
                  Release
                </Button>
              )}
              {memo.status === 'RELEASED' && (
                <Button onClick={handlePost} disabled={isProcessing} className="bg-green-600 hover:bg-green-700">
                  {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Post to Accounting
                </Button>
              )}
              {memo.status === 'POSTED' && (
                <Button variant="outline" onClick={() => navigate('/ar/open-items')}>
                  View AR Open Items
                </Button>
              )}
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Credit Memo Details */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Credit Memo Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Customer</span>
                <span className="font-medium">{memo.customer_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Credit Date</span>
                <span className="font-medium">{memo.credit_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Sales Org</span>
                <span className="font-medium">{memo.sales_organization}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Credit Reason</span>
                <span className="font-medium">{memo.credit_reason}</span>
              </div>
              {memo.credit_note && (
                <div>
                  <span className="text-gray-600">Note:</span>
                  <p className="text-sm mt-1">{memo.credit_note}</p>
                </div>
              )}
            </div>
          </Card>

          {/* References */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">References</h3>
            <div className="space-y-3">
              {memo.reference_returns_number && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Returns Order</span>
                  <button
                    onClick={() => navigate(`/otc/returns/${memo.reference_returns_number}`)}
                    className="font-mono text-orange-600 hover:underline"
                  >
                    {memo.reference_returns_number}
                  </button>
                </div>
              )}
              {memo.reference_billing_number && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Original Invoice</span>
                  <button
                    onClick={() => navigate(`/otc/billing/${memo.reference_billing_number}`)}
                    className="font-mono text-orange-600 hover:underline"
                  >
                    {memo.reference_billing_number}
                  </button>
                </div>
              )}
              {memo.accounting_document && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Accounting Doc</span>
                  <span className="font-mono font-medium text-green-600">{memo.accounting_document}</span>
                </div>
              )}
              {memo.posted_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Posted At</span>
                  <span className="text-sm">{new Date(memo.posted_at).toLocaleString()}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Values */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" /> Credit Values
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Net Value</span>
                <span className="font-mono font-medium text-lg">
                  ${memo.net_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Tax Amount</span>
                <span className="font-mono">
                  ${memo.tax_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-100 rounded-lg">
                <span className="text-green-900 font-semibold">Gross Value (Credit)</span>
                <span className="font-mono font-bold text-xl text-green-700">
                  ${memo.gross_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Currency</span>
                <span className="font-medium">{memo.currency}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Items Table */}
        <Card className="overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Package className="w-5 h-5" /> Credit Memo Items
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Material</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Credit Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Net Price</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Net Value</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Tax</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Gross Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {memo.items?.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono">{item.item_number.toString().padStart(4, '0')}</td>
                    <td className="px-4 py-3 font-mono text-sm">{item.material_id || '-'}</td>
                    <td className="px-4 py-3">{item.item_description}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {item.credit_quantity} {item.unit_of_measure}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      ${item.net_price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      ${item.net_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      ${item.tax_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-green-600">
                      ${item.gross_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-sm">{item.credit_reason}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 font-semibold">
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-right">Totals:</td>
                  <td className="px-4 py-3 text-right font-mono">
                    ${memo.net_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    ${memo.tax_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-green-600">
                    ${memo.gross_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>

        {/* Timestamps */}
        <div className="mt-6 text-sm text-gray-500">
          <p>Created: {memo.created_at ? new Date(memo.created_at).toLocaleString() : '-'}</p>
          <p>Updated: {memo.updated_at ? new Date(memo.updated_at).toLocaleString() : '-'}</p>
        </div>
      </div>
    </Layout>
  );
};

export default CreditMemoView;
